import React, { Fragment } from 'react';

import { Example } from '@af/design-system-docs-ui/example';
import { token } from '@atlaskit/tokens';

import Card from './components/token-card';

const elevationBackgroundChangeStylesCode = `
// Default:
Background color: elevation.surface
Hovered background color: elevation.surface.hovered
Pressed background color: elevation.surface.pressed

// Default (bordered):
Background color: elevation.surface
Hovered background color: elevation.surface.hovered
Pressed background color: elevation.surface.pressed
Border: 1px solid color.border

// Raised:
Background color: elevation.surface.raised
Hovered background color: elevation.surface.raised.hovered
Pressed background color: elevation.surface.raised.pressed
Shadow: elevation.shadow.raised

// Overlay
Background color: elevation.surface.overlay
Hovered background color: elevation.surface.overlay.hovered
Pressed background color: elevation.surface.overlay.pressed
Shadow: elevation.shadow.overlay

`;

const elevationChangeStylesCode = `
// Default:
Background color: elevation.surface
Hovered background color: elevation.surface.overlay
Pressed background color: elevation.surface.raised
Hovered shadow: elevation.shadow.overlay
Pressed shadow: elevation.shadow.raised

// Default (with border)
Background color: elevation.surface
Hovered background color: elevation.surface.overlay
Pressed background color: elevation.surface.raised
Border: 1px solid color.border
Hovered shadow: elevation.shadow.overlay
Pressed shadow: elevation.shadow.raised

// Raised
Background color: elevation.surface.raised
Hovered background color: elevation.surface.overlay
Pressed background color: elevation.surface.raised
Shadow: elevation.shadow.raised
Hovered shadow: elevation.shadow.overlay
Pressed shadow: elevation.shadow.raised
`;

const defaultBackgroundChangeStyles = {
	default: {
		label: 'Default',
		backgroundColor: token('elevation.surface'),
		hoverBackgroundColor: token('elevation.surface.hovered'),
		activeBackgroundColor: token('elevation.surface.pressed'),
		shadow: 'none',
	},
	defaultOutline: {
		label: 'Default (bordered)',
		backgroundColor: token('elevation.surface'),
		hoverBackgroundColor: token('elevation.surface.hovered'),
		activeBackgroundColor: token('elevation.surface.pressed'),
		shadow: 'none',
		border: `${token('border.width.selected')} solid ${token('color.border')}`,
	},
};
const backgroundChangeStyles = {
	raised: {
		label: 'Raised',
		backgroundColor: token('elevation.surface.raised'),
		hoverBackgroundColor: token('elevation.surface.raised.hovered'),
		activeBackgroundColor: token('elevation.surface.raised.pressed'),
		shadow: token('elevation.shadow.raised'),
	},
	overlay: {
		label: 'Overlay',
		backgroundColor: token('elevation.surface.overlay'),
		hoverBackgroundColor: token('elevation.surface.overlay.hovered'),
		activeBackgroundColor: token('elevation.surface.overlay.pressed'),
		shadow: token('elevation.shadow.overlay'),
	},
};

const elevationChangeStyles = {
	default: {
		label: 'Default',
		backgroundColor: token('elevation.surface'),
		hoverBackgroundColor: token('elevation.surface.overlay'),
		activeBackgroundColor: token('elevation.surface.raised'),
		shadow: 'none',
		hoverShadow: token('elevation.shadow.overlay'),
		activeShadow: token('elevation.shadow.raised'),
	},
	defaultOutline: {
		label: 'Default (bordered)',
		backgroundColor: token('elevation.surface'),
		hoverBackgroundColor: token('elevation.surface.overlay'),
		activeBackgroundColor: token('elevation.surface.raised'),
		border: `${token('border.width.selected')} solid ${token('color.border')}`,
		shadow: 'none',
		hoverShadow: token('elevation.shadow.overlay'),
		activeShadow: token('elevation.shadow.raised'),
	},
	raised: {
		label: 'Raised',
		backgroundColor: token('elevation.surface.raised'),
		hoverBackgroundColor: token('elevation.surface.overlay'),
		activeBackgroundColor: token('elevation.surface.raised'),
		shadow: token('elevation.shadow.raised'),
		hoverShadow: token('elevation.shadow.overlay'),
		activeShadow: token('elevation.shadow.raised'),
	},
};

const TokenElevationBackgroundChange = () => {
	return (
		<Fragment>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					display: 'flex',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					gap: '1.5rem',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					flexWrap: 'wrap',
				}}
			>
				{Object.entries(defaultBackgroundChangeStyles).map(([key, subStyle]) => (
					<Card key={key} tokenSet={subStyle} />
				))}
			</div>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					display: 'flex',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					marginTop: '1.5rem',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					gap: '1.5rem',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					flexWrap: 'wrap',
				}}
			>
				{Object.entries(backgroundChangeStyles).map(([key, subStyle]) => (
					<Card key={key} tokenSet={subStyle} />
				))}
			</div>
		</Fragment>
	);
};
const TokenElevationChange = () => {
	return (
		<Fragment>
			<div
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					display: 'flex',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					marginTop: '1.5rem',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					gap: '1.5rem',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					flexWrap: 'wrap',
				}}
			>
				{Object.entries(elevationChangeStyles).map(([key, subStyle]) => (
					<Card key={key} tokenSet={subStyle} />
				))}
			</div>
		</Fragment>
	);
};

// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Token elevation background change example__
 *
 * A token elevation background change example {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
export const TokenElevationBackgroundChangeExample = (): React.JSX.Element => {
	return (
		<Example
			Component={TokenElevationBackgroundChange}
			source={elevationBackgroundChangeStylesCode}
			packageName="@atlaskit/tokens"
		/>
	);
};

// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Token elevation change example__
 *
 * A token elevation change example {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
export const TokenElevationChangeExample = (): React.JSX.Element => {
	return (
		<Example
			Component={TokenElevationChange}
			source={elevationChangeStylesCode}
			packageName="@atlaskit/tokens"
		/>
	);
};
